import React, { useRef } from 'react';
import { Box, Typography, useTheme } from '@ublend-npm/aulaui-core';

import { HOME_VIEW_ROLE_LABELS, HomeViewRole } from '@app/constants/home';
import { HomeUpcomingAssignment } from '@core/assignments/api/homeUpcomingAssignments';
import InfiniteScrollTrigger from '../../../common/InfiniteScrollTrigger';
import WidgetCard from '../common/WidgetCard';
import UpcomingAssignmentRow from './UpcomingAssignmentRow';
import UpcomingAssignmentRowSkeleton from './UpcomingAssignmentRowSkeleton';

const STRINGS = {
  title: 'Upcoming assignments',
  empty: 'No upcoming assignments',
};

type UpcomingAssignmentsPageProps = {
  onClose: () => void;
  assignments: HomeUpcomingAssignment[];
  viewRole: HomeViewRole;
  isLoading?: boolean;
  getTermLabel?: (spaceId: string) => string | null;
  onOpenAssignment?: (
    assignment: HomeUpcomingAssignment,
    itemIndex: number
  ) => void | Promise<void>;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
};

const UpcomingAssignmentsPage = ({
  onClose,
  assignments,
  viewRole,
  isLoading = false,
  getTermLabel,
  onOpenAssignment,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: UpcomingAssignmentsPageProps) => {
  const { palette } = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', md: '800px' },
        margin: '0 auto',
        p: { xs: 2, md: 4 },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '100%',
        bgcolor: palette.background.default,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <WidgetCard
          title={STRINGS.title}
          minHeight="100%"
          contentGap={0}
          borderRadius="8px"
        >
          <Box
            ref={scrollContainerRef}
            sx={{ overflowY: 'auto', flex: 1, height: '100%', pr: 1 }}
          >
            {isLoading ? (
              <>
                <UpcomingAssignmentRowSkeleton />
                <UpcomingAssignmentRowSkeleton />
                <UpcomingAssignmentRowSkeleton showDivider={false} />
              </>
            ) : assignments.length ? (
              <>
                {assignments.map((assignment, index) => (
                  <UpcomingAssignmentRow
                    key={assignment.id}
                    assignment={assignment}
                    viewRole={viewRole}
                    termLabel={getTermLabel?.(assignment.spaceId) ?? null}
                    showDivider={index < assignments.length - 1 || hasMore}
                    itemIndex={index}
                    onOpen={onOpenAssignment}
                  />
                ))}

                {isLoadingMore && (
                  <UpcomingAssignmentRowSkeleton showDivider={false} />
                )}

                {onLoadMore && (
                  <InfiniteScrollTrigger
                    hasMore={hasMore}
                    isLoadingMore={isLoadingMore}
                    onLoadMore={onLoadMore}
                    rootRef={scrollContainerRef}
                  />
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" py={2}>
                {STRINGS.empty} in{' '}
                {HOME_VIEW_ROLE_LABELS[viewRole].toLowerCase()} view.
              </Typography>
            )}
          </Box>
        </WidgetCard>
      </Box>
    </Box>
  );
};

export default UpcomingAssignmentsPage;
